import { useCallback, useMemo } from "react";
import { useParams } from "react-router";

import { Form, Formik } from "formik";

import { FormStatus } from "components/form";
import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { isStatusReleaseStage, RELEASE_STAGES } from "hooks/tivoApps";
import { useUpdatePublications } from "hooks/tivoApps/useUpdatePublications";

import {
  PublicationsFormButtons,
  PublicationRenderer,
  PublicationsTable,
  PublicationSwitchCell,
} from "./components";
import { useTargets } from "./TargetsProvider";
import styles from "./TiVoPublicationsList.scss";
import { TiVoPublicationsForm, PublicationsTableProps } from "./types";
import {
  getPublicationsInitialValues,
  adaptPublicationsToRequest,
  getReleaseStagePublication,
} from "./utils";

export function TiVoPublicationsEditTable({
  platforms,
  publications,
  status,
}: PublicationsTableProps) {
  const { targets, setTargets } = useTargets();
  const { tabId: platformId } = useParams();
  const updatePublications = useUpdatePublications(platformId);
  const deviceData = useTiVoDeviceData();

  const initialValues = useMemo(
    () => getPublicationsInitialValues(platforms, publications),
    [platforms, publications]
  );

  const handleSubmit = useCallback(
    async (values: TiVoPublicationsForm) => {
      const data = adaptPublicationsToRequest(values);
      updatePublications.mutate(data);
    },
    [updatePublications]
  );

  const renderPublication: PublicationRenderer = ({
    publication,
    platformId,
    oemId,
    applicableReleaseStages,
    clientTypeId,
  }) => {
    const releaseStagePublication = getReleaseStagePublication(
      publication,
      platformId,
      oemId,
      clientTypeId
    );
    const variantReleaseStages =
      publication.variant?.status.filter(isStatusReleaseStage) || [];
    return RELEASE_STAGES.map((releaseStage) => {
      const name =
        `${publication.id}.${platformId}.${oemId}` +
        (clientTypeId ? `.${clientTypeId}` : "") +
        `.${releaseStage}`;

      const targets = (publication.variant?.versions || []).find((version) =>
        version.releaseStages?.includes(releaseStage)
      )?.targets;

      const isTargetValid = clientTypeId
        ? deviceData
            .getClientTypeIds([platformId], [oemId], targets)
            .includes(clientTypeId)
        : deviceData.getOemIds([platformId], targets).includes(oemId);

      return (
        <PublicationSwitchCell
          key={releaseStage}
          name={name}
          releaseStage={releaseStage}
          variantReleaseStages={variantReleaseStages}
          applicableReleaseStages={applicableReleaseStages}
          publication={releaseStagePublication}
          isTargetValid={isTargetValid}
        />
      );
    });
  };

  return (
    <RestrictedArea allowed={[ROLES.tivo.moderator]}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <FormStatus
              submitMutations={[updatePublications]}
              withErrorDetails
            />
            <PublicationsTable
              cellClassName={styles.editCell}
              platforms={platforms}
              publications={publications}
              status={status}
              targets={targets}
              setTargets={setTargets}
            >
              {renderPublication}
            </PublicationsTable>
            <PublicationsFormButtons {...formikProps} />
          </Form>
        )}
      </Formik>
    </RestrictedArea>
  );
}
